import React, { useEffect, useState } from 'react';
import { useToaster } from '../context/ToasterContext'; // Adjust path if needed
import { FaInfoCircle } from 'react-icons/fa';

export default function ToasterBar() {
  const { toasts, removeToast } = useToaster();

  return (
    <div style={styles.container}>
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          removeToast={removeToast}
        />
      ))}
    </div>
  );
}

/**
 * A single toast item component
 */
function ToastItem({ toast, removeToast }) {
  const [exiting, setExiting] = useState(false);

  // Destructure duration from toast
  const { id, message, type, duration } = toast;

  // Use toast.duration rather than a hardcoded 3000
  const durationMs = duration; 

  useEffect(() => {
    // 1) Start a timer that sets "exiting" ~0.3s before the actual removal
    //    so we have time to animate out
    const fadeOutTime = durationMs - 300;
    const fadeTimer = setTimeout(() => {
      setExiting(true);
    }, fadeOutTime);

    // 2) Remove the toast fully after `durationMs`
    const removeTimer = setTimeout(() => {
      removeToast(id);
    }, durationMs);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [id, durationMs, removeToast]);

  return (
    <div
      style={{
        ...styles.toast,
        ...stylesBase(type),
        animation: exiting
          ? `${animations.slideUp} 0.3s forwards`
          : `${animations.bounceIn} 0.5s`,
      }}
    >
      {/* Left icon (green check, red X, or blue info) */}
      <div style={styles.iconWrapper}>{renderIcon(type)}</div>

      {/* The message text */}
      <div style={styles.message}>{message}</div>

      {/* Close (X) button on the right */}
      <button style={styles.closeBtn} onClick={() => removeToast(id)}>
        ×
      </button>

      {/* Progress bar at the bottom */}
      <div style={styles.progressBarWrapper}>
        {/* The colored bar that animates width from 0→100% */}
        <div
          style={{
            ...styles.progressBar,
            backgroundColor:
              type === 'error'
                ? '#ff5f58'
                : type === 'info'
                ? '#2196f3'
                : '#4caf50',
            animation: `${animations.fillProgress} linear ${durationMs}ms forwards`,
          }}
        />
      </div>
    </div>
  );
}

/**
 * Renders the icon for success/error/info
 */
function renderIcon(type) {
  if (type === 'error') {
    // A simple "X" or “close” circle
    return <span style={{ color: '#ff5f58', fontWeight: 'bold' }}>✕</span>;
  }
  if (type === 'info') {
    return <FaInfoCircle style={{ color: '#2196f3' }} />;
  }
  // Default to success checkmark
  return <span style={{ color: '#4caf50', fontWeight: 'bold' }}>✔</span>;
}

/**
 * Base styling logic: different border-left or icon color, etc.
 */
function stylesBase(type) {
  if (type === 'error') {
    return { borderLeft: '5px solid #ff5f58' };
  }
  if (type === 'info') {
    return { borderLeft: '5px solid #2196f3' };
  }
  // default (success)
  return { borderLeft: '5px solid #4caf50' };
}

const styles = {
  container: {
    position: 'fixed',
    top: 20,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    zIndex: 9999,
  },
  toast: {
    position: 'relative',
    width: '300px',
    marginBottom: '1rem',
    backgroundColor: '#fff',
    color: '#333',
    borderRadius: '6px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    overflow: 'hidden', // so progress bar doesn't pop out
  },
  iconWrapper: {
    marginRight: '0.75rem',
    fontSize: '1.25rem',
    display: 'flex',
    alignItems: 'center',
  },
  message: {
    flex: 1,
    fontSize: '0.9rem',
    paddingRight: '0.5rem',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: '1.2rem',
    color: '#999',
    cursor: 'pointer',
    lineHeight: '1',
  },
  progressBarWrapper: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '4px',
    backgroundColor: 'transparent',
  },
  progressBar: {
    height: '100%',
    width: '0%', // will animate to 100%
  },
};

/**
 * CSS animations as strings for inline usage
 * (We define them using @keyframes below in a <style> tag or an internal object).
 */
const animations = {
  // 1) bounceIn
  bounceIn: 'toast-bounceIn',
  // 2) slideUp (fade out up)
  slideUp: 'toast-slideUp',
  // 3) progress fill
  fillProgress: 'toast-fillProgress',
};

/** 
 * You can't do @keyframes in JS objects easily,
 * so we inject them into the page once (just an example).
 */
const styleSheet = `
@keyframes ${animations.bounceIn} {
  0% {
    opacity: 0;
    transform: translateY(-50px);
  }
  60% {
    opacity: 1;
    transform: translateY(10px);
  }
  80% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0);
  }
}
@keyframes ${animations.slideUp} {
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(-20px);
  }
}
@keyframes ${animations.fillProgress} {
  0% {
    width: 0%;
  }
  100% {
    width: 100%;
  }
}
`;

// Inject the keyframe styles into the document's head
if (typeof document !== 'undefined') {
  const sheet = document.createElement('style');
  sheet.innerHTML = styleSheet;
  document.head.appendChild(sheet);
}
